/**
 * Clause Library API for Word Add-in.
 *
 * Endpoints under /api/v1/negotiations/clauses/
 */

import { apiFetch } from './api'

// ── Types ──────────────────────────────────────────────────

export type ClauseCategory =
  | 'LIABILITY'
  | 'FORCE_MAJEURE'
  | 'TERMINATION'
  | 'CONFIDENTIALITY'
  | 'GDPR'
  | 'IP'
  | 'PAYMENT'
  | 'DISPUTE'

export type ClauseStatus = 'DRAFT' | 'APPROVED' | 'DEPRECATED'

export interface ClauseActorSummary {
  koblink_id: string
  display_name: string
}

export interface Clause {
  id: number
  title: string
  body: string
  language: string
  category: ClauseCategory
  tags: string[]
  status: ClauseStatus
  version: number
  organization: string | null
  organization_display: ClauseActorSummary | null
  created_by: string
  created_by_display: ClauseActorSummary | null
  created_at: string
  updated_at: string
}

export interface ClauseListParams {
  category?: ClauseCategory
  status?: ClauseStatus
  language?: string
  search?: string
}

// ── API Functions ──────────────────────────────────────────

export async function listClauses(params?: ClauseListParams): Promise<Clause[]> {
  const searchParams = new URLSearchParams()
  if (params?.category) searchParams.set('category', params.category)
  if (params?.status) searchParams.set('status', params.status)
  if (params?.language) searchParams.set('language', params.language)
  if (params?.search) searchParams.set('search', params.search)

  const qs = searchParams.toString()
  const url = `/negotiations/clauses/${qs ? `?${qs}` : ''}`
  const res = await apiFetch(url)
  if (!res.ok) throw new Error(`Failed to list clauses: ${res.status}`)
  return res.json()
}

export async function suggestClauses(params?: {
  category?: ClauseCategory
  language?: string
  limit?: number
}): Promise<Clause[]> {
  const searchParams = new URLSearchParams()
  if (params?.category) searchParams.set('category', params.category)
  if (params?.language) searchParams.set('language', params.language)
  if (params?.limit) searchParams.set('limit', String(params.limit))

  const qs = searchParams.toString()
  const url = `/negotiations/clauses/suggest/${qs ? `?${qs}` : ''}`
  const res = await apiFetch(url)
  if (!res.ok) throw new Error(`Failed to suggest clauses: ${res.status}`)
  return res.json()
}
